<!--
  Copyright (C) 2022 - Jeci SARL - https://jeci.fr

  This program is free software: you can redistribute it and/or modify
  it under the terms of the GNU Affero General Public License as
  published by the Free Software Foundation, either version 3 of the
  License, or (at your option) any later version.

  This program is distributed in the hope that it will be useful,
  but WITHOUT ANY WARRANTY; without even the implied warranty of
  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
  GNU Affero General Public License for more details.

  You should have received a copy of the GNU Affero General Public License
  along with this program.  If not, see https://www.gnu.org/licenses/agpl-3.0.html.
-->

<template>
  <div class="flex flex-column gap-3">
    <div class="form-field">
      <label class="form-label" for="code">{{
        $t("emplacementForm.code")
      }}</label>
      <PrimeInputText
        id="code"
        v-model="editableEmplacement.code"
        :disabled="isEditMode"
        class="w-full"
      />
      <small class="form-help">{{ $t("emplacementForm.codeHelp") }}</small>
    </div>

    <div class="form-field">
      <label class="form-label" for="type">
        {{ $t("emplacementForm.type") }}
        <a
          v-if="isTypeReadOnly"
          href="#"
          class="type-edit-link"
          @click.prevent="isTypeReadOnly = false"
          >{{ $t("emplacementForm.editType") }}</a
        >
      </label>
      <PrimeInputText
        id="type"
        v-model="editableEmplacement.type"
        :disabled="isTypeReadOnly"
        class="w-full"
        :class="[{ 'type-readonly': isTypeReadOnly }]"
      />
      <small class="form-help">{{ $t("emplacementForm.typeHelp") }}</small>
    </div>

    <div class="form-field">
      <label class="form-label" for="aspects">{{
        $t("emplacementForm.aspects")
      }}</label>
      <PrimeMultiSelect
        id="aspects"
        v-model="selectedAspects"
        :options="availableAspects"
        :placeholder="$t('emplacementForm.selectAspects')"
        optionLabel="title"
        optionValue="id"
        :filterFields="['id', 'title']"
        class="w-full"
        filter
      >
        <template #option="slotProps">
          <div>
            <div class="font-semibold">{{ slotProps.option.id }}</div>
            <small class="text-color-secondary">{{
              slotProps.option.title
            }}</small>
          </div>
        </template>
      </PrimeMultiSelect>
    </div>

    <div class="form-field">
      <label class="form-label">{{ $t("emplacementForm.metadata") }}</label>
      <div
        v-if="availableProperties.length === 0"
        class="text-center p-3 text-color-secondary"
      >
        {{ $t("emplacementForm.noMetadata") }}
      </div>
      <PrimeDataTable
        v-else
        :value="editableMetadata"
        stripedRows
        size="small"
        class="metadata-table"
      >
        <PrimeColumn field="key" :header="$t('emplacementForm.key')">
          <template #body="{ data }">
            <strong>{{ data.key }}</strong>
            <div class="form-help">{{ data.title }}</div>
          </template>
        </PrimeColumn>
        <PrimeColumn
          :header="$t('emplacementForm.mappingType')"
          headerStyle="width: 10rem"
        >
          <template #body="{ data }">
            <PrimeDropdown
              v-model="data.type"
              :options="metadataTypes"
              :placeholder="$t('emplacementForm.mappingType')"
              class="w-full"
              @change="onMetadataTypeChange(data)"
            />
          </template>
        </PrimeColumn>
        <PrimeColumn :header="$t('emplacementForm.value')">
          <template #body="{ data }">
            <PrimeInputText
              v-if="data.type !== 'parent' && data.type !== 'ignored'"
              v-model="data.value"
              :placeholder="$t('emplacementForm.valueOrFieldName')"
              class="w-full"
            />
          </template>
        </PrimeColumn>
        <PrimeColumn
          :header="$t('emplacementForm.required')"
          headerStyle="width: 5rem; text-align: center"
          bodyStyle="text-align: center"
        >
          <template #body="{ data }">
            <span
              v-if="
                data.type !== 'parent' &&
                data.type !== 'template' &&
                data.type !== 'ignored'
              "
              v-tooltip.top="
                isFieldOptional(data)
                  ? $t('emplacementForm.optionalFieldTooltip')
                  : null
              "
            >
              <PrimeInputSwitch
                v-model="data.required"
                :disabled="isFieldOptional(data)"
              />
            </span>
          </template>
        </PrimeColumn>
      </PrimeDataTable>
    </div>
  </div>
</template>

<script>
import InputText from "primevue/inputtext";
import Dropdown from "primevue/dropdown";
import MultiSelect from "primevue/multiselect";
import InputSwitch from "primevue/inputswitch";
import DataTable from "primevue/datatable";
import Column from "primevue/column";
import Tooltip from "primevue/tooltip";
import { useToast } from "primevue/usetoast";
import alfrescoModelService from "@/services/AlfrescoModelService.js";
import { useI18n } from "vue-i18n";

const newEmplacementTemplate = () => ({
  code: "",
  type: "cm:folder",
  aspects: [],
  metadata: {},
});

export default {
  name: "EmplacementForm",
  components: {
    PrimeInputText: InputText,
    PrimeDropdown: Dropdown,
    PrimeMultiSelect: MultiSelect,
    PrimeInputSwitch: InputSwitch,
    PrimeDataTable: DataTable,
    PrimeColumn: Column,
  },
  directives: {
    tooltip: Tooltip,
  },
  props: {
    emplacement: { type: Object, default: null },
    fields: { type: Array, default: () => [] },
  },
  emits: ["save"],
  setup() {
    const toast = useToast();
    const { t } = useI18n();
    return { toast, t };
  },
  data() {
    return {
      editableEmplacement: newEmplacementTemplate(),
      editableMetadata: [],
      isEditMode: false,
      isTypeReadOnly: true,
      metadataTypes: ["ignored", "value", "field", "template", "parent"],
      availableProperties: [],
      selectedAspects: [],
      availableAspects: [],
    };
  },
  watch: {
    emplacement: {
      handler(newVal) {
        if (newVal && newVal.code) {
          this.isEditMode = true;
          this.editableEmplacement = JSON.parse(JSON.stringify(newVal));
        } else {
          this.isEditMode = false;
          this.editableEmplacement = newEmplacementTemplate();
        }
        this.isTypeReadOnly = true;
        // Restore selected aspects from emplacement
        this.selectedAspects = this.editableEmplacement.aspects || [];
        this.metadataToObjectArray(this.editableEmplacement.metadata);
      },
      immediate: true,
    },
    "editableEmplacement.type": {
      handler(newType) {
        if (newType) {
          this.loadAllProperties();
        }
      },
      immediate: true,
    },
    selectedAspects: {
      handler() {
        this.loadAllProperties();
      },
    },
  },
  mounted() {
    this.loadAvailableAspects();
  },
  methods: {
    isFieldOptional(data) {
      if (data.type === "field") {
        const field = this.fields.find((f) => f.name === data.value);
        if (field && field.optional === true) {
          data.required = false;
          return true;
        }
      }
      return false;
    },
    async loadAvailableAspects() {
      try {
        const aspectsResponse = await alfrescoModelService.getAspects({
          maxItems: 1000,
        });
        if (aspectsResponse.list?.entries) {
          this.availableAspects = aspectsResponse.list.entries.map((entry) => ({
            id: entry.entry.id,
            title: entry.entry.title || entry.entry.id,
          }));
        }
      } catch (error) {
        console.error("Failed to load available aspects", error);
        this.availableAspects = [];
      }
    },
    async loadAllProperties() {
      const allProperties = [];

      // Load properties from the document type
      if (this.editableEmplacement.type) {
        try {
          const typeResponse = await alfrescoModelService.getType(
            this.editableEmplacement.type,
          );
          const typeProperties = alfrescoModelService.extractPropertiesFromType(
            typeResponse.entry,
          );
          allProperties.push(
            ...typeProperties.map((prop) => ({
              ...prop,
              source: `Type: ${this.editableEmplacement.type}`,
            })),
          );
        } catch (error) {
          console.error(
            "Failed to load properties for type",
            this.editableEmplacement.type,
            error,
          );
        }
      }

      // Load properties from selected aspects
      if (this.selectedAspects && this.selectedAspects.length > 0) {
        for (const aspectId of this.selectedAspects) {
          try {
            const aspectResponse =
              await alfrescoModelService.getAspect(aspectId);
            const aspectProperties =
              alfrescoModelService.extractPropertiesFromAspect(
                aspectResponse.entry,
              );
            allProperties.push(
              ...aspectProperties.map((prop) => ({
                ...prop,
                source: `Aspect: ${aspectId}`,
              })),
            );
          } catch (error) {
            console.error(
              "Failed to load properties for aspect",
              aspectId,
              error,
            );
          }
        }
      }

      this.availableProperties = allProperties;

      // Auto-generate editableMetadata from available properties
      this.generateMetadataFromProperties();
    },
    generateMetadataFromProperties() {
      // Get existing metadata configuration to preserve user settings
      const existingMetadata = {};
      this.editableMetadata.forEach((meta) => {
        existingMetadata[meta.key] = {
          type: meta.type,
          value: meta.value,
          required: meta.required,
          title: meta.title,
        };
      });

      // Generate metadata list from available properties
      const newMetadata = this.availableProperties.map((prop) => {
        // If metadata already exists, preserve its configuration
        if (existingMetadata[prop.key]) {
          return {
            key: prop.key,
            title: prop.title, // Use the fresh title from properties
            type: existingMetadata[prop.key].type,
            value: existingMetadata[prop.key].value,
            required: existingMetadata[prop.key].required || false,
          };
        }

        // Otherwise, create new metadata with default "ignored" type
        return {
          key: prop.key,
          title: prop.title,
          type: "ignored",
          value: "",
          required: false,
        };
      });

      // Also preserve metadata that was configured but is no longer in available properties
      // (this can happen if user changes document type or removes aspects)
      this.editableMetadata.forEach((meta) => {
        if (
          meta.type !== "ignored" &&
          !this.availableProperties.find((p) => p.key === meta.key)
        ) {
          newMetadata.push({
            key: meta.key,
            title: meta.title || meta.key,
            type: meta.type,
            value: meta.value,
            required: meta.required,
          });
        }
      });

      this.editableMetadata = newMetadata;
    },
    metadataToObjectArray(metadata) {
      if (!metadata) {
        this.editableMetadata = [];
        return;
      }

      // Store existing metadata configuration temporarily
      const existingConfig = Object.entries(metadata).reduce(
        (acc, [key, valueObj]) => {
          const type = Object.keys(valueObj).find((k) => k !== "required");
          const value = valueObj[type];
          const required = valueObj.required || false;
          acc[key] = { type, value, required };
          return acc;
        },
        {},
      );

      // If we have available properties, regenerate from them
      // This will be called again after loadAllProperties() completes
      if (this.availableProperties.length > 0) {
        this.editableMetadata = this.availableProperties.map((prop) => {
          const config = existingConfig[prop.key] || {
            type: "ignored",
            value: "",
            required: false,
          };
          return {
            key: prop.key,
            title: prop.title,
            type: config.type,
            value: config.value,
            required: config.required,
          };
        });
      } else {
        // Fallback: use metadata keys without titles (will be updated when properties load)
        this.editableMetadata = Object.entries(metadata).map(
          ([key, valueObj]) => {
            const type = Object.keys(valueObj).find((k) => k !== "required");
            const value = valueObj[type];
            const required = valueObj.required || false;
            return { key, type, value, required, title: key };
          },
        );
      }
    },
    metadataArrayToObject() {
      return this.editableMetadata.reduce((obj, meta) => {
        // Skip metadata with type "ignored"
        if (meta.key && meta.type !== "ignored") {
          obj[meta.key] = { [meta.type]: meta.value };
          if (
            meta.required &&
            meta.type !== "parent" &&
            meta.type !== "template"
          ) {
            obj[meta.key].required = true;
          }
        }
        return obj;
      }, {});
    },
    onMetadataTypeChange(meta) {
      // When type is changed to "field", auto-fill value with the property name without prefix
      if (meta.type === "field" && meta.key && !meta.value) {
        // Extract property name after the last colon (e.g., "cm:title" -> "title")
        const parts = meta.key.split(":");
        if (parts.length > 1) {
          meta.value = parts[parts.length - 1];
        } else {
          meta.value = meta.key;
        }
      }
    },
    submit() {
      if (!this.editableEmplacement.code) {
        this.toast.add({
          severity: "warn",
          summary: this.t("emplacementForm.validation.codeRequired"),
          life: 4000,
        });
        return;
      }
      const finalEmplacement = {
        ...this.editableEmplacement,
        aspects: this.selectedAspects,
        metadata: this.metadataArrayToObject(),
      };
      this.$emit("save", finalEmplacement);
    },
  },
};
</script>

<style scoped>
.form-field {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.form-label {
  font-size: 0.75rem;
  color: var(--p-text-muted-color, #6b7280);
  font-weight: 600;
}

.form-help {
  font-size: 0.75rem;
  color: var(--p-text-muted-color, #6b7280);
  font-style: italic;
}

.type-edit-link {
  font-size: 0.75rem;
  font-weight: 400;
  margin-left: 0.5rem;
}

.type-readonly {
  background-color: var(--p-surface-100, #f3f4f6) !important;
  opacity: 0.8;
}

.metadata-table :deep(.p-datatable-header-cell) {
  font-size: 0.75rem;
}
</style>
